import SellerBoxLogo from '@/assets/icons/SellerboxLogo.svg?react';
import { cn } from '@/utils/tailwind';
import { useLayoutEffect, useRef, useState } from 'react';
import { SigninEmailForm, useSigninEmailForm } from './signin-email-form';
import { SigninPhoneForm, useSigninPhoneForm } from './signin-phone-form';
import { ChevronRightIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@/routes';
import { AnimatePresence, motion } from 'framer-motion';
import { useLogEvent } from '@/hooks/use-log-event';
import { GA_EVENT_NAMES, GA_ROUTES } from '@/constants/ga';
import { Button } from '@/components/atoms/NewPcButton';

type SigninContainerProps = {
  variant?: 'desktop' | 'mobile';
  defaultRedirectPath?: string;
  onMockSignin?: () => void;
};

type SigninTab = 'email' | 'phone';

const CONTENT_HEIGHT_TRANSITION = {
  duration: 0.34,
  ease: [0.22, 1, 0.36, 1],
};

const CONTENT_TRANSITION = {
  duration: 0.24,
  ease: [0.22, 1, 0.36, 1],
};

const FORM_ACTION_GAP_HEIGHT: Record<'desktop' | 'mobile', Record<SigninTab, number>> = {
  desktop: {
    email: 20,
    phone: 76,
  },
  mobile: {
    email: 20,
    phone: 20,
  },
};

export const SigninContainer = ({
  variant = 'desktop',
  defaultRedirectPath = ROUTES.root,
  onMockSignin,
}: SigninContainerProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<SigninTab>('email');
  const formContentRef = useRef<HTMLDivElement>(null);
  const [formAreaHeight, setFormAreaHeight] = useState<number | 'auto'>('auto');
  const isMobileVariant = variant === 'mobile';
  const formVariant = isMobileVariant ? 'mobile' : 'desktop';
  const redirectPath = searchParams.get('redirect');
  const signupPath =
    isMobileVariant && redirectPath
      ? `${ROUTES.mobileSignup}?redirect=${encodeURIComponent(redirectPath)}`
      : isMobileVariant
      ? ROUTES.mobileSignup
      : ROUTES.signup;
  const resetPasswordPath =
    isMobileVariant && redirectPath
      ? `${ROUTES.mobileResetPassword}?redirect=${encodeURIComponent(redirectPath)}`
      : isMobileVariant
      ? ROUTES.mobileResetPassword
      : ROUTES.resetPassword;
  const emailForm = useSigninEmailForm({
    defaultRedirectPath,
    resetPasswordPath,
    onMockSignin,
  });
  const phoneForm = useSigninPhoneForm({
    defaultRedirectPath,
    isActive: tab === 'phone',
    onMockSignin,
  });
  const activeFormAction = tab === 'email' ? emailForm.action : phoneForm.action;

  const { logEvent } = useLogEvent();

  useLayoutEffect(() => {
    const element = formContentRef.current;
    if (!element) return;

    const updateFormHeight = () => {
      setFormAreaHeight(element.offsetHeight + FORM_ACTION_GAP_HEIGHT[formVariant][tab]);
    };

    updateFormHeight();

    const observer = new ResizeObserver(updateFormHeight);
    observer.observe(element);

    return () => observer.disconnect();
  }, [formVariant, tab]);

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-8 overflow-hidden bg-white text-center',
        isMobileVariant ? 'w-full' : 'w-[480px] rounded-[20px] p-10 max-md:p-5',
      )}
    >
      <div className={cn('flex flex-col items-center', isMobileVariant ? 'gap-3' : 'gap-4')}>
        <SellerBoxLogo />
        <div className="text-gr-700 Title3S18">
          마켓마다 로그인 없이
          <br />
          나의 모든 데이터를 한번에 조회하세요
        </div>
      </div>

      <div className="w-full space-y-3">
        <div className="relative flex w-full overflow-hidden rounded-md bg-gr-200 p-[5px] text-gr-800 Body4S14">
          {/* 이동하는 흰색 배경 */}
          <motion.div
            className="absolute left-[5px] top-[5px] h-10 w-[calc(50%-5px)] rounded-[10px] bg-white shadow-sm"
            animate={{
              x: tab === 'email' ? 0 : '100%',
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
            }}
          />
          <button
            type="button"
            aria-pressed={tab === 'email'}
            className={cn(
              'relative z-10 h-10 flex-1 rounded-[10px] text-gr-800 Body1S16 focus:outline-none focus-visible:ring-2 focus-visible:ring-point-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gr-200 max-md:Body4S14',
            )}
            onClick={() => {
              setTab('email');
              logEvent({
                eventName: GA_EVENT_NAMES.login.tabSelected.email,
                pageTitle: GA_ROUTES.login,
              });
            }}
          >
            이메일 로그인
          </button>
          <button
            type="button"
            aria-pressed={tab === 'phone'}
            className={cn(
              'relative z-10 h-10 flex-1 rounded-[10px] text-gr-800 Body1S16 focus:outline-none focus-visible:ring-2 focus-visible:ring-point-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gr-200 max-md:Body4S14',
            )}
            onClick={() => {
              setTab('phone');
              logEvent({
                eventName: GA_EVENT_NAMES.login.tabSelected.phone,
                pageTitle: GA_ROUTES.login,
              });
            }}
          >
            휴대폰 번호 로그인
          </button>
        </div>

        <motion.div
          initial={false}
          animate={{ height: formAreaHeight }}
          transition={CONTENT_HEIGHT_TRANSITION}
          className="overflow-hidden"
        >
          <div ref={formContentRef}>
            <AnimatePresence initial={false} mode="popLayout">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 10, scale: 0.985, filter: 'blur(4px)' }}
                animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -8, scale: 0.985, filter: 'blur(4px)' }}
                transition={CONTENT_TRANSITION}
              >
                {tab === 'email' ? (
                  <SigninEmailForm form={emailForm} />
                ) : (
                  <SigninPhoneForm form={phoneForm} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        <Button
          type="button"
          className="w-full"
          disabled={activeFormAction.disabled}
          isLoading={activeFormAction.isLoading}
          onClick={activeFormAction.onSubmit}
        >
          로그인
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <div className="text-gr-600 Body5M14">아직 셀러박스 회원이 아니신가요?</div>
        <button
          type="button"
          className="flex items-center gap-1 text-gr-700 Body5M14"
          onClick={() => {
            logEvent({ eventName: GA_EVENT_NAMES.login.toSignup, pageTitle: GA_ROUTES.login });
            navigate(signupPath);
          }}
        >
          회원가입
          <ChevronRightIcon width={16} height={16} />
        </button>
      </div>
    </div>
  );
};
